import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, ShoppingBag, ShoppingCart, Phone, X, Flame, User } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useMerchCart } from "@/context/MerchCartContext";
import { BusinessStatusBadge } from "@/components/BusinessStatusBadge";
import { useBusinessStatus } from "@/context/BusinessStatusContext";
import { useData } from "@/context/DataContext";
import { useAuth } from "@/context/AuthContext";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

const links = [
  { to: "/", label: "Home" },
  { to: "/menu", label: "Menu" },
  { to: "/catering", label: "Catering" },
  { to: "/story", label: "Our Story" },
  { to: "/careers", label: "Careers" },
  { to: "/merch", label: "Merch" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const { count, setOpen } = useCart();
  const { count: merchCount, setOpen: setMerchOpen } = useMerchCart();
  const { status: bizStatus } = useBusinessStatus();
  const orderLabel = bizStatus?.status === "closed" ? "Pre-Order" : "Order Now";
  const { settings } = useData();
  const { user, login } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const phone = settings?.phone || "207-773-8000";
  const firstName = (user?.name || "").split(" ")[0];

  const go = (to) => { setMobileOpen(false); navigate(to); };

  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#0A2818]/85 border-b border-emerald-800/40 safe-top">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 md:h-20 flex items-center justify-between gap-3">
        <Link to="/" data-testid="nav-brand-logo" className="flex items-center gap-2.5 group">
          {settings?.logo_image ? (
            <span className="grid place-items-center w-11 h-11 md:w-12 md:h-12 rounded-full bg-white overflow-hidden ring-2 ring-amber-400/60 shrink-0">
              <img src={settings.logo_image} alt="Sir. King Kitchen logo" className="w-full h-full object-contain" />
            </span>
          ) : (
            <span className="grid place-items-center w-9 h-9 rounded-full bg-amber-400 text-[#0A2818]">
              <Flame className="w-5 h-5" />
            </span>
          )}
          <span className="font-display font-black tracking-tight text-lg md:text-2xl leading-none">
            <span className="text-amber-400">Sir. King</span> <span className="text-[#FDFBF7]">Kitchen</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
              className={`font-display uppercase tracking-wide text-sm font-bold transition-colors ${
                pathname === l.to ? "text-amber-400" : "text-[#FDFBF7]/80 hover:text-amber-300"
              }`}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <div className="hidden md:block"><BusinessStatusBadge /></div>
          <a
            href={`tel:${phone.replace(/[^0-9]/g, "")}`}
            data-testid="nav-call-button"
            className="hidden sm:flex items-center gap-2 px-3 py-2 rounded-full border border-emerald-700/50 text-amber-300 text-sm font-semibold hover:bg-emerald-900/60 transition-colors"
          >
            <Phone className="w-4 h-4" /> {phone}
          </a>
          <button
            data-testid="nav-cart-trigger"
            onClick={() => setOpen(true)}
            title="Food order cart"
            className="relative grid place-items-center w-11 h-11 rounded-full bg-emerald-900/70 text-amber-300 hover:bg-emerald-800 active:scale-95 transition-transform"
          >
            <ShoppingBag className="w-5 h-5" />
            {count > 0 && (
              <span className="absolute -top-1 -right-1 bg-amber-400 text-[#0A2818] text-[11px] font-black w-5 h-5 grid place-items-center rounded-full">
                {count}
              </span>
            )}
          </button>
          {(merchCount > 0 || pathname.startsWith("/merch")) && (
            <button
              data-testid="nav-merch-cart-trigger"
              onClick={() => setMerchOpen(true)}
              title="Merch cart"
              className="relative grid place-items-center w-11 h-11 rounded-full bg-emerald-900/70 text-amber-300 hover:bg-emerald-800 active:scale-95 transition-transform"
            >
              <ShoppingCart className="w-5 h-5" />
              {merchCount > 0 && (
                <span data-testid="nav-merch-cart-count" className="absolute -top-1 -right-1 bg-amber-400 text-[#0A2818] text-[11px] font-black w-5 h-5 grid place-items-center rounded-full">
                  {merchCount}
                </span>
              )}
            </button>
          )}
          {user ? (
            <button
              data-testid="nav-account-button"
              onClick={() => navigate("/account")}
              className="flex items-center gap-2 px-3 py-2 rounded-full border border-emerald-700/50 text-amber-300 text-sm font-semibold hover:bg-emerald-900/60 transition-colors"
            >
              <User className="w-4 h-4" /> <span className="hidden md:inline">{firstName || "Account"}</span>
            </button>
          ) : (
            <button
              data-testid="nav-login-button"
              onClick={() => login("/account")}
              className="flex items-center gap-2 px-3 py-2 rounded-full border border-emerald-700/50 text-amber-300 text-sm font-semibold hover:bg-emerald-900/60 transition-colors"
            >
              <User className="w-4 h-4" /> <span className="hidden sm:inline">Sign In</span>
            </button>
          )}
          <button
            data-testid="nav-order-now-button"
            onClick={() => navigate("/menu")}
            className="hidden md:inline-flex px-5 py-2.5 rounded-full bg-amber-400 hover:bg-amber-300 text-[#0A2818] font-display font-black uppercase tracking-wide text-sm shadow-lg shadow-amber-500/20 active:scale-95 transition-transform"
          >
            {orderLabel}
          </button>

          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <button data-testid="nav-mobile-toggle" className="lg:hidden grid place-items-center w-11 h-11 rounded-full bg-emerald-900/70 text-amber-300 active:scale-95 transition-transform">
                <Menu className="w-5 h-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-[#0A2818] border-emerald-800 text-[#FDFBF7] w-[85%]">
              <div className="flex items-center justify-between mb-8">
                <span className="font-display font-black text-xl text-amber-400">Sir. King Kitchen</span>
                <button onClick={() => setMobileOpen(false)}><X className="w-6 h-6" /></button>
              </div>
              <div className="mb-5"><BusinessStatusBadge /></div>
              <div className="flex flex-col gap-1">
                {links.map((l) => (
                  <button
                    key={l.to}
                    data-testid={`mobile-nav-${l.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                    onClick={() => go(l.to)}
                    className="text-left font-display uppercase tracking-wide text-lg font-bold py-3 border-b border-emerald-800/50 hover:text-amber-400"
                  >
                    {l.label}
                  </button>
                ))}
                <button onClick={() => go("/track")} className="text-left font-display uppercase tracking-wide text-lg font-bold py-3 border-b border-emerald-800/50 hover:text-amber-400">Track Order</button>
                {user ? (
                  <button data-testid="mobile-nav-account" onClick={() => go("/account")} className="text-left font-display uppercase tracking-wide text-lg font-bold py-3 border-b border-emerald-800/50 hover:text-amber-400">My Account</button>
                ) : (
                  <button data-testid="mobile-nav-login" onClick={() => { setMobileOpen(false); login("/account"); }} className="text-left font-display uppercase tracking-wide text-lg font-bold py-3 border-b border-emerald-800/50 hover:text-amber-400">Sign In</button>
                )}
              </div>
              <button
                onClick={() => go("/menu")}
                className="mt-8 w-full py-4 rounded-full bg-amber-400 text-[#0A2818] font-display font-black uppercase tracking-wide"
              >
                {orderLabel}
              </button>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
